// Minimal IndexedDB helper for local-first asset bytes

import { isAssetPersistenceEnabled } from "./assetStoragePreference";
import { logger } from "./logger";

const DB_NAME = "critter-factory-assets";
const STORE = "files";
const MANIFEST_STORE = "manifest";
const FILE_META_STORE = "fileMeta";
const VERSION = 3;
const MAX_STORED_FILE_COUNT = 1000;
const MAX_STORED_BYTES = 512 * 1024 * 1024;

type FileMeta = {
  key: string;
  bytes: number;
  updatedAt: number;
};

const ASSET_PERSISTENCE_UNAVAILABLE_MESSAGE =
  "IndexedDB asset persistence is unavailable in this browser session.";

let indexedDbAvailability: "unknown" | "available" | "unavailable" = "unknown";
let indexedDbUnavailableWarned = false;

function createPersistenceUnavailableError(): Error {
  return new Error(ASSET_PERSISTENCE_UNAVAILABLE_MESSAGE);
}

function markIndexedDbUnavailable(reason?: unknown): Error {
  indexedDbAvailability = "unavailable";
  if (!indexedDbUnavailableWarned) {
    indexedDbUnavailableWarned = true;
    logger.warn(
      "[idb] IndexedDB asset persistence is unavailable; uploaded assets will use session-only blob URLs.",
      reason
    );
  }
  return createPersistenceUnavailableError();
}

function getIndexedDbFactory(): IDBFactory | null {
  try {
    return globalThis.indexedDB ?? null;
  } catch (error) {
    markIndexedDbUnavailable(error);
    return null;
  }
}

function withDB<T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    if (indexedDbAvailability === "unavailable") {
      reject(createPersistenceUnavailableError());
      return;
    }

    const idb = getIndexedDbFactory();
    if (!idb) {
      reject(markIndexedDbUnavailable());
      return;
    }

    let req: IDBOpenDBRequest;
    try {
      req = idb.open(DB_NAME, VERSION);
    } catch (error) {
      reject(markIndexedDbUnavailable(error));
      return;
    }

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) db.createObjectStore(MANIFEST_STORE);
      if (!db.objectStoreNames.contains(FILE_META_STORE)) db.createObjectStore(FILE_META_STORE);
    };
    req.onerror = () => reject(markIndexedDbUnavailable(req.error));
    req.onsuccess = () => {
      indexedDbAvailability = "available";
      const db = req.result;
      fn(db)
        .then((v) => resolve(v))
        .catch(reject)
        .finally(() => db.close());
    };
  });
}

function byteLength(value: Blob | ArrayBuffer): number {
  return value instanceof Blob ? value.size : value.byteLength;
}

export async function putBytes(key: string, value: Blob | ArrayBuffer): Promise<void> {
  if (!isAssetPersistenceEnabled()) {
    throw new Error("Asset persistence is disabled.");
  }
  await withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction([STORE, FILE_META_STORE], "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        const store = tx.objectStore(STORE);
        const metaStore = tx.objectStore(FILE_META_STORE);
        const v = value instanceof Blob ? value : new Blob([value]);
        store.put(v, key);
        metaStore.put(
          { key, bytes: byteLength(value), updatedAt: Date.now() } satisfies FileMeta,
          key
        );
      })
  );
  await pruneStoredBytes().catch(() => {});
}

export async function putBytesMany(
  entries: Array<{ key: string; value: Blob | ArrayBuffer }>
): Promise<void> {
  if (entries.length === 0) return Promise.resolve();
  if (!isAssetPersistenceEnabled()) {
    throw new Error("Asset persistence is disabled.");
  }
  await withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction([STORE, FILE_META_STORE], "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        const store = tx.objectStore(STORE);
        const metaStore = tx.objectStore(FILE_META_STORE);
        const now = Date.now();
        entries.forEach(({ key, value }) => {
          const v = value instanceof Blob ? value : new Blob([value]);
          store.put(v, key);
          metaStore.put({ key, bytes: byteLength(value), updatedAt: now } satisfies FileMeta, key);
        });
      })
  );
  await pruneStoredBytes().catch(() => {});
}

export function getBytes(key: string): Promise<Blob | null> {
  return withDB<Blob | null>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const store = tx.objectStore(STORE);
        const req = store.get(key);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
          const v = req.result as Blob | undefined;
          resolve(v || null);
        };
      })
  ).catch((error) => {
    if (isPersistenceUnavailableError(error)) return null;
    throw error;
  });
}

function isPersistenceUnavailableError(error: unknown): boolean {
  return error instanceof Error && error.message === ASSET_PERSISTENCE_UNAVAILABLE_MESSAGE;
}

function loadFileMeta(db: IDBDatabase): Promise<FileMeta[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_META_STORE, "readonly");
    const store = tx.objectStore(FILE_META_STORE);
    const req = store.getAll();
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const result = Array.isArray(req.result) ? req.result : [];
      resolve(
        result
          .map((entry) => ({
            key: String((entry as Partial<FileMeta>)?.key || ""),
            bytes: Number((entry as Partial<FileMeta>)?.bytes) || 0,
            updatedAt: Number((entry as Partial<FileMeta>)?.updatedAt) || 0,
          }))
          .filter((entry) => entry.key.length > 0)
      );
    };
  });
}

function deleteStoredFiles(keys: string[]): Promise<void> {
  if (keys.length === 0) return Promise.resolve();
  return withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction([STORE, FILE_META_STORE], "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        const fileStore = tx.objectStore(STORE);
        const metaStore = tx.objectStore(FILE_META_STORE);
        keys.forEach((key) => {
          fileStore.delete(key);
          metaStore.delete(key);
        });
      })
  );
}

async function pruneStoredBytes(): Promise<void> {
  const meta = await withDB(loadFileMeta);
  if (meta.length <= MAX_STORED_FILE_COUNT) {
    const totalBytes = meta.reduce((sum, entry) => sum + entry.bytes, 0);
    if (totalBytes <= MAX_STORED_BYTES) return;
  }

  const newestFirst = [...meta].sort((a, b) => b.updatedAt - a.updatedAt);
  const keysToDelete: string[] = [];
  let retainedBytes = 0;
  let retainedCount = 0;

  newestFirst.forEach((entry) => {
    const canKeepByCount = retainedCount < MAX_STORED_FILE_COUNT;
    const canKeepByBytes = retainedCount === 0 || retainedBytes + entry.bytes <= MAX_STORED_BYTES;
    if (canKeepByCount && canKeepByBytes) {
      retainedBytes += entry.bytes;
      retainedCount += 1;
      return;
    }
    keysToDelete.push(entry.key);
  });

  await deleteStoredFiles(keysToDelete);
}

// Save entire manifest (array of entries) into MANIFEST_STORE under key 'entries'
export function saveManifest(entries: unknown[]): Promise<void> {
  if (!isAssetPersistenceEnabled()) return Promise.resolve();
  return withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(MANIFEST_STORE, "readwrite");
        const store = tx.objectStore(MANIFEST_STORE);
        const req = store.put(entries || [], "entries");
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve();
      })
  ).catch((error) => {
    if (isPersistenceUnavailableError(error)) return;
    throw error;
  });
}

export function loadManifest(): Promise<unknown[] | null> {
  if (!isAssetPersistenceEnabled()) return Promise.resolve(null);
  return withDB<unknown[] | null>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(MANIFEST_STORE, "readonly");
        const store = tx.objectStore(MANIFEST_STORE);
        const req = store.get("entries");
        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
          resolve(Array.isArray(req.result) ? req.result : null);
        };
      })
  ).catch((error) => {
    if (isPersistenceUnavailableError(error)) return null;
    throw error;
  });
}

// Danger: clears all stored asset bytes
export function clearFilesStore(): Promise<void> {
  return withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction([STORE, FILE_META_STORE], "readwrite");
        tx.onerror = () => reject(tx.error);
        tx.oncomplete = () => resolve();
        tx.objectStore(STORE).clear();
        tx.objectStore(FILE_META_STORE).clear();
      })
  ).catch((error) => {
    if (isPersistenceUnavailableError(error)) return;
    throw error;
  });
}

// Danger: clears the persisted manifest
export function clearManifestStore(): Promise<void> {
  return withDB<void>(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(MANIFEST_STORE, "readwrite");
        const store = tx.objectStore(MANIFEST_STORE);
        const req = store.clear();
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve();
      })
  ).catch((error) => {
    if (isPersistenceUnavailableError(error)) return;
    throw error;
  });
}

// Convenience: clear both bytes and manifest
export async function clearAllAssets(): Promise<void> {
  await clearFilesStore();
  await clearManifestStore();
}
